/**
 * CronStudio — the Crons facet (ADR-111 IA-1), embedded by FleetEditor.tsx for
 * one agent at a time. Hosts cron management (list / create / edit / delete via
 * CronAuthorPanel, always visible) and Convert-a-cron→workflow (CronConvertPanel,
 * a toggle). Fetches its own roster + core-agent set (core agents are excluded
 * from the authoring picker, ADR-103) + /fleet version (ADR-101 live refresh).
 */
import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import { useFleetVersion } from '../hooks/useFleetVersion'
import { CronAuthorPanel } from './CronAuthorPanel'
import { CronConvertPanel } from './CronConvertPanel'

export function CronStudio({
  canPropose,
  scopedAgent,
  isCoreOwned,
  initialName,
}: {
  canPropose: boolean
  // ADR-111 IA-1: the Crons facet always operates scoped to one agent — the
  // agent picker in CronAuthorPanel is locked to it.
  scopedAgent?: string
  // ADR-103: the scoped agent is core-owned — its crons are managed in core, so
  // the authoring form is not offered. The LIST still is: reading a core agent's
  // crons is exactly what an operator does before asking core to change one.
  isCoreOwned?: boolean
  // The cron named in the URL (/fleet-editor/agents/:agent/crons/:name), opened
  // for edit once the list has loaded. Sharing a cron with a colleague is a
  // copied address bar, the same as sharing a workflow.
  initialName?: string
}) {
  const [roster, setRoster] = useState<string[]>([])
  const [coreAgents, setCoreAgents] = useState<string[]>([])
  const [convertOpen, setConvertOpen] = useState(false)
  const fleetSha = useFleetVersion()

  useEffect(() => {
    let live = true
    api.listAgents().then(r => { if (live) setRoster((r.agents ?? []).slice().sort()) }).catch(() => {})
    api.listCoreAgents().then(r => { if (live) setCoreAgents(r.agents ?? []) }).catch(() => {})
    return () => { live = false }
  }, [])

  // Core agents can't own fleet-authored crons (ADR-103) — keep them out of the picker.
  const authorableRoster = useMemo(() => roster.filter(a => !coreAgents.includes(a)), [roster, coreAgents])

  return (
    <div className="space-y-4 h-full flex flex-col">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Crons</h1>
          <p className="text-sm text-gray-500 mt-1">
            Schedule this agent's work — create, edit, or delete a cron (a prompt or a workflow), or convert an existing prompt-cron into a deterministic workflow. Changes are proposed for in-app review; nothing runs until you approve it.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setConvertOpen(v => !v)}
            className="px-3 py-1.5 text-sm border border-purple-300 text-purple-700 rounded hover:bg-purple-50"
            title="Convert a scheduled prompt-cron into a workflow (ADR-066/080)"
          >
            ⤳ Convert cron
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto space-y-4">
        {convertOpen && <CronConvertPanel canPropose={canPropose} onClose={() => setConvertOpen(false)} />}
        <CronAuthorPanel
          canPropose={canPropose}
          roster={authorableRoster}
          scopedAgent={scopedAgent}
          isCoreOwned={isCoreOwned}
          initialName={initialName}
          fleetSha={fleetSha}
        />
      </div>
    </div>
  )
}
